import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import Link from "@/components/link";
import { ApiError } from "@/lib/api";
import { meQuery, useMe } from "@/lib/queries";
import { useNavStyle } from "@/lib/nav-style";
import { syncShopifyNow } from "@/lib/actions";
import { ROLE_LABELS } from "@shared/permissions";
import { SIDEBAR_HEADER_H } from "@shared/nav-style";
import { BrandLogo } from "@/components/brand-logo";
import { SidebarNav } from "@/components/sidebar-nav";
import { SidebarTree } from "@/components/sidebar-tree";
import { NavStyleToggle } from "@/components/nav-style-toggle";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import { ThemeToggle } from "@/components/theme-toggle";
import { HeaderClock } from "@/components/header-clock";
import { DataFreshness } from "@/components/data-freshness";

// The signed-in shell: /api/me first (a 401 goes to /login), then the
// sidebar, header and mobile tab bar around every report page.

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: "/login" });
      throw err;
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const me = useMe();
  const navStyle = useNavStyle();
  const { user, staff } = me;

  if (!staff) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="font-heading text-lg font-semibold text-foreground">No dashboard access</h1>
          <p className="mt-2 text-sm text-muted">
            {user.email} is signed in but is not an active team member. Ask an admin to add you, then sign in again.
          </p>
          <form action="/auth/signout" method="post" className="mt-4">
            <button className="text-xs text-accent-dark hover:underline">Sign out</button>
          </form>
        </div>
      </div>
    );
  }

  const sideNav = me.nav;
  const freshness = me.freshness ? (
    <DataFreshness
      lastSyncAt={me.freshness.lastSyncAt}
      lastError={me.freshness.lastError}
      newestOrderAt={me.freshness.newestOrderAt}
      canSync={me.freshness.canSync}
      onSync={me.freshness.canSync ? syncShopifyNow : undefined}
    />
  ) : null;

  const sidebarHeader = (
    <div className={`flex ${SIDEBAR_HEADER_H} shrink-0 flex-col justify-center border-b border-sidebar-border px-5`}>
      <BrandLogo variant="tile" className="w-[164px]" />
      <span className="mt-2 block font-heading text-[11px] font-semibold uppercase tracking-widest text-accent">Dashboard</span>
    </div>
  );
  const sidebarFooter = (
    <div className="p-4 border-t border-sidebar-border text-xs">
      {staff.fullName && <div className="truncate font-medium">{staff.fullName}</div>}
      <div className={`truncate ${staff.fullName ? "text-sidebar-muted" : "font-medium"}`}>{staff.email}</div>
      <div className="text-sidebar-muted">{ROLE_LABELS[staff.role]}</div>
      <form action="/auth/signout" method="post" className="mt-2">
        <button className="text-sidebar-muted hover:text-sidebar-foreground transition-colors">Sign out</button>
      </form>
      <p className="mt-3 text-[10px] leading-snug text-sidebar-muted/80">
        <Link href="/terms" className="hover:text-sidebar-foreground transition-colors">
          Terms of service
        </Link>
        <span className="mx-1.5" aria-hidden>
          &middot;
        </span>
        &copy; {new Date().getFullYear()} Digital King INC
      </p>
    </div>
  );

  return (
    <div className="flex h-dvh overflow-hidden">
      {navStyle === "classic" ? (
        <SidebarTree items={sideNav} header={sidebarHeader} footer={sidebarFooter} />
      ) : (
        <SidebarNav items={sideNav} header={sidebarHeader} footer={sidebarFooter} tiles={navStyle === "tiles"} />
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* relative z-30: backdrop-blur makes the header its own stacking
            layer, so without a z-index page content paints over its menus. */}
        <header className="relative z-30 h-14 shrink-0 border-b border-border bg-surface/70 backdrop-blur flex items-center gap-3 px-4 md:px-6">
          <BrandLogo className="h-5 w-auto md:hidden dark:hidden" />
          <span className="hidden dark:max-md:block"><BrandLogo variant="tile" className="h-4" tileClassName="px-2 py-1" /></span>
          <HeaderClock />
          <div className="ml-auto flex items-center gap-3">
            {freshness}
            <NavStyleToggle value={navStyle} />
            <ThemeToggle />
            <form action="/auth/signout" method="post" className="md:hidden">
              <button className="text-xs font-medium text-muted hover:text-foreground">Sign out</button>
            </form>
          </div>
        </header>
        <main className="@container flex-1 overflow-y-auto px-4 pt-4 pb-24 sm:px-6 sm:pt-6 md:pb-8 lg:px-8 lg:pt-8">
          <Outlet />
        </main>
      </div>

      <MobileTabBar items={sideNav} />
    </div>
  );
}
